import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { FileText, Trash2 } from 'lucide-react';

export default function ClaimDraftsModal({
  open, onOpenChange,
  claimDrafts, maxDrafts,
  editingDraft, setEditingDraft,
  onLoadDraft, onUpdateDraft, onDeleteDraft,
  formatCurrency,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Saved Claim Drafts ({claimDrafts.length}/{maxDrafts})
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {claimDrafts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No saved drafts</p>
              <p className="text-sm">Save your claim items as drafts to use them later</p>
            </div>
          ) : (
            <div className="space-y-3">
              {claimDrafts.map((draft) => (
                <div
                  key={draft.id}
                  className={`p-4 border rounded-lg ${editingDraft === draft.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-800">{draft.clientName}</h4>
                        {draft.clientId && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            {draft.clientId}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {draft.items.length} items • Total: {formatCurrency(draft.totalAmount)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Saved: {new Date(draft.createdAt).toLocaleDateString()} {new Date(draft.createdAt).toLocaleTimeString()}
                        {draft.updatedAt !== draft.createdAt && (
                          <> • Updated: {new Date(draft.updatedAt).toLocaleDateString()}</>
                        )}
                      </p>
                      <div className="mt-2 text-xs text-gray-500">
                        {draft.items.slice(0, 3).map((item, i) => (
                          <span key={i}>
                            {item.item_name} ({item.quantity}x){i < Math.min(draft.items.length - 1, 2) ? ', ' : ''}
                          </span>
                        ))}
                        {draft.items.length > 3 && <span>... +{draft.items.length - 3} more</span>}
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onLoadDraft(draft)}
                        className="text-green-600 border-green-300 hover:bg-green-50"
                      >
                        Load
                      </Button>
                      {editingDraft === draft.id ? (
                        <Button
                          size="sm"
                          onClick={() => onUpdateDraft(draft.id)}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          Save Update
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            onLoadDraft(draft);
                            setEditingDraft(draft.id);
                            toast.info('Edit the items and click "Save Update" in drafts');
                          }}
                          className="text-blue-600 border-blue-300 hover:bg-blue-50"
                        >
                          Edit
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onDeleteDraft(draft.id)}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {editingDraft && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              <strong>Editing mode:</strong> Load a draft, modify items, then click "Save Update" to save changes.
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditingDraft(null)}
                className="ml-2 text-yellow-700 hover:text-yellow-800"
              >
                Cancel Edit
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
